import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiV2, Code } from '@.api';
import { useDictionaries } from '@.hooks';
import { Layout } from '@.layout';
import { LinkHelper } from '@/Services';
import { SpClusterFormSupportResource, SpClusterResource } from '@/Types/serviceProviderClusters';

import { InitialClusterState, StoreClusterForm, StoreClusterModel } from '../components/StoreClusterForm';

export const StoreClusterEditPage = () => {
    const { id } = useParams();

    const [storeClusterDetail, setStoreClusterDetail] = useState<SpClusterResource | null>(null);
    const [attachedStoresToStoreCluster, setAttachedStoresToStoreCluster] = useState<number[]>([]);

    const navigate = useNavigate();

    const [formSupport, setFormSupport] = useState<SpClusterFormSupportResource>();

    const [dictionaries] = useDictionaries('Companies', 'StoreGroups', 'Stores');

    const companies = dictionaries?.companies || [];
    const storeGroups = dictionaries?.store_groups || [];
    const stores = dictionaries?.stores || [];

    const busyCompaniesIds: number[] = companies
        .filter((company) => {
            return !formSupport?.available_companies_ids?.includes(company.id);
        })
        .map((company) => company.id);

    const busyStoreGroupsIds: number[] = storeGroups
        .filter((storeGroup) => {
            return !formSupport?.available_store_groups_ids?.includes(storeGroup.id);
        })
        .map((storeGroup) => storeGroup.id);

    const busyStoresIds: number[] = stores
        .filter((store) => {
            return !formSupport?.available_store_groups_ids?.includes(store.id);
        })
        .map((store) => store.id);

    const editStoreCluster = async (model: StoreClusterModel) => {
        const response = await ApiV2.serviceProviderClusters().updateCluster(Number(id), {
            name: model.name,
            stores_ids: model.stores,
        });

        if (response.code !== Code.Success) {
            return;
        }

        navigate(LinkHelper.serviceProvider.storeClusterList);
    };

    const initialState = useMemo<InitialClusterState>(() => {
        const selectedStores =
            dictionaries?.stores?.filter((store) => {
                return attachedStoresToStoreCluster.includes(store.id);
            }) || [];

        return {
            selectedStores,
            storeClusterName: storeClusterDetail?.name || '',
        };
    }, [attachedStoresToStoreCluster, dictionaries?.stores, storeClusterDetail?.name]);

    useEffect(() => {
        const getFormSupport = async () => {
            const response = await ApiV2.serviceProviderClusters().formSupport();

            if (response.code !== Code.Success) {
                return;
            }

            setFormSupport(response.data.form_support);
        };

        getFormSupport();
    }, []);

    useEffect(() => {
        const getStoreCluster = async () => {
            const response = await ApiV2.serviceProviderClusters().getClusterDetail(Number(id));

            if (response.code !== Code.Success) {
                return;
            }

            setStoreClusterDetail(response.data.sp_cluster);
        };

        getStoreCluster();
    }, [id]);

    useEffect(() => {
        const getAttachedStoresToCluster = async () => {
            const response = await ApiV2.serviceProviderClusters().formSupport({ sp_cluster_id: Number(id) });

            if (response.code !== Code.Success) {
                return;
            }

            setAttachedStoresToStoreCluster(response.data.form_support.attached_stores_ids);
        };

        getAttachedStoresToCluster();
    }, [id]);

    return (
        <Layout nobg>
            <StoreClusterForm
                title="Create store cluster"
                companies={companies}
                stores={stores}
                storeGroups={storeGroups}
                busyCompaniesIds={busyCompaniesIds}
                busyStoreGroupsIds={busyStoreGroupsIds}
                busyStoresIds={busyStoresIds}
                onSave={editStoreCluster}
                initialState={initialState}
            />
        </Layout>
    );
};
